import React, {useEffect} from 'react';
import {TextField, Button, StepContent, StepLabel, Stepper, Step, Alert, Box} from "@mui/material";
import {useForm} from "react-hook-form";
import CopyContentToClipboard from "../../utils/CopyContentToClipboard";

import DomainAlert from './DomainAlert';

function getSteps() {
    return ['Enter your custom domain', 'Set up a CNAME', 'Add & Verify DNS'];
}

export const GetDomainParts = (hostname) => {
    const parts = hostname?.split(".");

    if (parts?.length < 2) {
        // Invalid or localhost
        return {subdomain: "@", domain: hostname};
    }

    if (parts?.length === 2) {
        // Only root domain
        return {subdomain: "@", domain: hostname};
    }

    const domain = parts?.slice(-2)?.join(".");
    const subdomain = parts?.slice(0, -2)?.join(".");

    return {subdomain: subdomain || "@", domain};
}

export default function AddTrackingDomain(props) {
    const {register, handleSubmit, formState: {errors}, getValues} = useForm();

    const {activeStep, trackingDomain} = props?.state;
    const {setActiveStep, setTrackingDomain} = props;

    const [domain, setDomain] = React.useState("");

    const steps = getSteps();

    const handleNext = () => {
        setActiveStep(activeStep + 1);
    };

    const handleBack = () => {
        setActiveStep(activeStep - 1);
    };

    const onSubmit = (formData) => {
        setTrackingDomain(formData?.trackingDomain);
        setActiveStep(activeStep + 1);
    };

    const handleAddAndVerify = () => {
        props.addAndVerify(trackingDomain);
    };

    useEffect(() => {
        if (activeStep === 1) {
            setDomain(GetDomainParts(getValues("trackingDomain"))?.subdomain);
        }
    }, [activeStep, getValues]);

    return (
        <Box sx={{width: "100%"}}>
            <Stepper activeStep={activeStep} orientation="vertical">
                {steps?.map((label, index) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                        <StepContent>
                            {index === 0 &&
                                <>
                                    <Box pt={1} pb={1}>
                                        If you don't have a domain, it is required to purchase one and make sure that
                                        this
                                        domain is ready. You can buy your domain through any domain registrar, e.g.
                                        <strong> GoDaddy.com</strong>, <strong>NameCheap.com</strong> or <strong>CloudFlare.com</strong>.
                                    </Box>

                                    <Alert severity="warning" variant="filled" sx={{marginTop: 2, marginBottom: 2}}>
                                        We recommend using subdomains instead of root domains to help prevent
                                        verification and SSL certificate errors.
                                    </Alert>

                                    <TextField
                                        error={!!errors.trackingDomain}
                                        helperText={errors.trackingDomain ? "Enter a valid domain, such as go.example.com." : "Use a subdomain without https:// or a path."}
                                        variant="outlined"
                                        size="small"
                                        margin="dense"
                                        id="trackingDomain"
                                        {...register("trackingDomain", {
                                            required: true,
                                            pattern: /^((?:(?:(?:\w[.\-+]?)*)\w)+)((?:(?:(?:\w[.\-+]?){0,62})\w)+)\.(\w{2,10})$/,
                                            maxLength: 255
                                        })}
                                        name="trackingDomain"
                                        type="text"
                                        placeholder="go.example.com"
                                        label="Custom tracking domain"
                                        fullWidth={true}
                                        defaultValue={trackingDomain}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </>
                            }

                            {index === 1 &&
                                <Box>
                                    <p>
                                        To create a CNAME record, please go to your domain management platform and paste
                                        the info below:
                                    </p>
                                    <Box>
                                        <Box><CopyContentToClipboard label="Type" copyText="CNAME"/></Box>
                                        <Box><CopyContentToClipboard label="Name/Host" copyText={domain}/></Box>
                                        <Box><CopyContentToClipboard label="Target" copyText="skrotrack.com"/></Box>
                                    </Box>

                                    <Alert severity="warning" variant="filled" sx={{mt: 2, mb: 2}}>
                                        If your are using CloudFlare domain, then you will need to
                                        create a CNAME record to your subdomain and turn proxy off.
                                    </Alert>
                                </Box>
                            }

                            {index === 2 &&
                                <Box pt={1} pb={1}>
                                    Please note that updating DNS records may take up to 24 hours, but usually it
                                    takes 15-20 minutes.
                                </Box>
                            }

                            {index === 2 && props.submissionError && <DomainAlert severity="error">{props.submissionError}</DomainAlert>}

                            <Box mb={2}>
                                <>
                                    {activeStep > 0 && activeStep < 3 &&
                                        <Button disabled={props.state.isLoading} onClick={handleSubmit(handleBack)} sx={{mt: 1, mb: 1}}>
                                            Back
                                        </Button>
                                    }
                                    {activeStep === 0 &&
                                        <Button disabled={props.state.isLoading}
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSubmit(onSubmit)}
                                            sx={{mt: 1, mb: 1}}
                                        >
                                            Next
                                        </Button>
                                    }
                                    {activeStep === 1 &&
                                        <Button disabled={props.state.isLoading}
                                            variant="contained"
                                            color="primary"
                                            onClick={handleNext}
                                            sx={{mt: 1, mb: 1}}
                                        >
                                            Next
                                        </Button>
                                    }
                                    {activeStep === 2 && (
                                        <Button disabled={props.state.isLoading}
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSubmit(handleAddAndVerify)}
                                            sx={{mt: 1, mb: 1}}
                                        >
                                            Add & Verify
                                        </Button>)
                                    }
                                </>
                            </Box>
                        </StepContent>
                    </Step>
                ))}
            </Stepper>
        </Box>
    );
}
